import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { getUserOrgId } from "../getUserOrgId";
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import deleteOrganisation from "../http/deleteOrganisation";
import { applyOrgDeletedCacheUpdates } from "./deleteCacheUpdates";
import deleteOrganisationUser from "../http/deleteOrganisationUser";
import getOrganisation from "../http/getOrganisation";
import postOrganisation from "../http/postOrganisation";
import postOrganisationUser, {
  OrganisationUserPayload,
} from "../http/postOrganisationUser";
import putOrganisation from "../http/putOrganisation";
import {
  KindeUser,
  Organisation,
  OrgSiteListEntry,
} from "../models/organisation";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

const useOrganisation = (orgId?: string, userOrgId?: string) => {
  const { getToken, getClaim } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  const queryClient = useQueryClient();
  const fetchOrganisation: UseQueryResult<
    { organisation: Organisation; sites: OrgSiteListEntry[] },
    unknown
  > = useQuery(
    ["org", orgId],
    async () => {
      return getOrganisation(
        await getAuthTokenBlocking(getToken, relogin),
        orgId!,
        await getUserOrgId(getClaim, userOrgId)
      );
    },
    { enabled: !!orgId, staleTime: 5 * 60 * 1000 }
  );

  const updateOrganisation: UseMutationResult<
    Organisation,
    unknown,
    { organisation: Partial<Organisation> },
    unknown
  > = useMutation(
    async (payload: { organisation: Partial<Organisation> }) =>
      putOrganisation(
        await getAuthTokenBlocking(getToken, relogin),
        orgId!,
        payload
      ),
    {
      onSuccess: (_) => {
        queryClient.invalidateQueries(["org", orgId]);
        queryClient.invalidateQueries(["orgs"]);
      },
    }
  );

  const addOrgUser: UseMutationResult<
    void,
    unknown,
    OrganisationUserPayload,
    unknown
  > = useMutation(
    async (data: OrganisationUserPayload) => {
      return postOrganisationUser(
        await getAuthTokenBlocking(getToken, relogin),
        orgId!,
        data,
        await getUserOrgId(getClaim, userOrgId)
      );
    },
    {
      onSuccess: (_) => {
        queryClient.invalidateQueries(["org", orgId]);
      },
    }
  );

  const removeOrgUser: UseMutationResult<void, unknown, KindeUser, unknown> =
    useMutation(
      async (user: KindeUser) => {
        return deleteOrganisationUser(
          await getAuthTokenBlocking(getToken, relogin),
          orgId!,
          user
        );
      },
      {
        onSuccess: (_) => {
          queryClient.invalidateQueries(["org", orgId]);
        },
      }
    );

  const invalidateOrg = () => {
    queryClient.invalidateQueries(["org", orgId]);
  };

  const createOrg: UseMutationResult<
    Organisation,
    unknown,
    { organisation: Partial<Organisation> },
    unknown
  > = useMutation(async (payload: { organisation: Partial<Organisation> }) =>
    postOrganisation(await getAuthTokenBlocking(getToken, relogin), payload)
  );

  // Hard delete the org. The BE cascades to siteOwnerships and any
  // sites that become orphaned (no remaining ownerships); the admin
  // app gates the button to empty orgs (no SiteOwnership rows + no
  // users) so this should not delete sites in practice.
  const removeOrg: UseMutationResult<void, unknown, void, unknown> =
    useMutation(
      async () =>
        deleteOrganisation(
          await getAuthTokenBlocking(getToken, relogin),
          orgId!
        ),
      {
        onSuccess: () => applyOrgDeletedCacheUpdates(queryClient, orgId!),
      }
    );

  return {
    addOrgUser,
    removeOrgUser,
    removeOrg,
    createOrg,
    fetchOrganisation,
    updateOrganisation,
    invalidateOrg,
  };
};

export default useOrganisation;
